import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  effect,
  inject
} from '@angular/core';
import { VersionReadyEvent } from '@angular/service-worker';
import { useRaygun } from '@approot/app.raygun.setup';
import { GlobalWebsocketService } from '@approot/global-websocket';
import {
  ActivityState,
  Alert,
  AlertType,
  Strategy,
  Trade,
  UserPreferencesState,
  UserPreferencesStateKey
} from '@approot/models';
import { MarketDataService } from '@approot/monitoring/market-data/market-data.service';
import { PNLBlotterService } from '@approot/positions-detail/pnl-blotter/pnl-blotter.service';
import { PNLPivotService } from '@approot/positions-detail/pnl-blotter/pnl-pivot-table/pnl-pivot.service';
import { BackToBackService } from '@approot/positions-detail/white-label-blotter/back-to-back-trades/back-to-back-trades.service';
import { ProviderQuoteService } from '@approot/provider-quote';
import { RfqMessagesService } from '@approot/rfq-messages/rfq-messages.service';
import {
  AdminService,
  AlertsService,
  AuthService,
  ComponentVisibilityService,
  ConfigService,
  EntityMapperService,
  MarketsService,
  RfqService,
  StrategiesService,
  StrategyDetailService,
  SwUpdateService,
  TradesCaptureService,
  UserStorageService
} from '@approot/services';
import { VersionCheckerService } from '@approot/services/version-checker.service';
import { openClose } from '@approot/shared/animations';
import { SubscriptionSink, Utilities, sleep } from '@approot/utilities';
import {
  DisplayDefinitionsService,
  StrategiesViewerComponent,
  StrategyDetailComponent
} from '@approot/widgets';
import { WindowIndexService } from '@approot/widgets/rfq/rfq-container/window-index.service';
import { RfqTimelineService } from '@approot/widgets/rfq/services/rfq-timeline.service';
import { environment } from '@root/environments/environment';
import { Throttle } from 'lodash-decorators';
import rg4js from 'raygun4js';
import { Observable, firstValueFrom, fromEvent, interval, merge } from 'rxjs';
import { filter, map, tap, throttleTime } from 'rxjs/operators';
import { CdkPortalOutlet, ComponentPortal } from '@angular/cdk/portal';
import { PortalService } from '@approot/widgets/strategy-detail/modals/quote-schedule/quote-schedule-portal.service';
import { QuoteScheduleComponent } from '@approot/widgets/strategy-detail/modals/quote-schedule/quote-schedule.component';

type TradeBookedNotificationPayload = {
  // TODO: type legs properly
  legs: { tradeId: string; productType: string }[];
};

type NotificationPayload = {
  messageContent: string;
  messageType: AlertType;
};

@Component({
  selector: 'app-exotics',
  templateUrl: './exotics.component.html',
  styleUrls: ['./exotics.component.scss'],
  animations: [openClose]
})
export class ExoticsComponent implements OnInit, OnDestroy, AfterViewInit {
  public forwardCurve: any;
  public alerts: Observable<Alert[]>;
  public userPrefOpen = false;
  public playSounds = false;
  public modalZIndex = 10000;
  public activityState: ActivityState = 'ACTIVE';
  public canLogin = true;
  public medusaConnectionStateIntensity = 0;
  public medusaConnectionCountdown: string = null;
  public pnlBlotterStateChanged$ = this.pnlBlotterService.stateChanged;
  public pnlPivotStateChanged$ = this.pnlPivotService.stateChanged;
  public backToBackStateChanged$ = this.backToBackService.stateChanged;
  public isDragging = false;
  @ViewChild(StrategyDetailComponent)
  public strategyDetailComponent: StrategyDetailComponent;
  @ViewChild(StrategiesViewerComponent)
  public strategiesViewerComponent: StrategiesViewerComponent;
  public isBackToBackGridOpen = false;
  public isProd: boolean = Utilities.currentEnvironment() === 'prod';

  private subsink = new SubscriptionSink();
  private medusaLoginTimer: any = null;
  private strategyDetailService = inject(StrategyDetailService);
  private medusaConnectionState = this.rfqService.connectionState;
  private medusaConnectionCountdownTimer: any = null;

  constructor(
    private componentVisibilityService: ComponentVisibilityService,
    private strategiesService: StrategiesService,
    private tradesCaptureService: TradesCaptureService,
    private adminService: AdminService,
    private marketsService: MarketsService,
    private alertsService: AlertsService,
    private rfqService: RfqService,
    private providerQuoteService: ProviderQuoteService,
    private displayDefinitionsService: DisplayDefinitionsService,
    private swUpdate: SwUpdateService,
    private authService: AuthService,
    private configService: ConfigService,
    private globalSocket: GlobalWebsocketService,
    private winService: WindowIndexService,
    private userStorageService: UserStorageService,
    private pnlBlotterService: PNLBlotterService,
    private pnlPivotService: PNLPivotService,
    private backToBackService: BackToBackService,
    public portalService: PortalService,
    public rfqTimelineService: RfqTimelineService,
    private cdRef: ChangeDetectorRef
  ) {
    inject(VersionCheckerService);
    inject(EntityMapperService);
    inject(RfqMessagesService);
    inject(MarketDataService);
    setTimeout(() => this.isApiWorking(), 3000);
    this.adminService.setTeamMembers();
    this.doSwUpdate();
    this.subsink.sink = this.userStorageService
      .storeChangesObservable<UserPreferencesState>(UserPreferencesStateKey)
      .subscribe(prefs => {
        this.playSounds = !!prefs?.OtherSettings?.sounds;
      });

    this.handleStrategiesListener();
    this.handleTradeBookedListener();
    this.notificationListener();
    this.pageVisibilityListener();
    this.maybeLogUserIn();

    effect(
      () => (this.isBackToBackGridOpen = this.backToBackService.isGridOpen())
    );

    effect(() => this.handleConnectionStateChange());
  }

  public ngOnDestroy() {
    this.subsink.unsubscribe();
    clearTimeout(this.medusaLoginTimer);
  }

  countArray = [];

  public ngOnInit() {
    if (environment.featureFlags.localDev) {
      this.loadTestStrategy();
    }
    this.setupRaygun();
    this.activityListener();

    this.portalService.countChanges$
      .pipe(
        map(count => {
          console.log('detected countChanges', count);
          this.countArray = Array(count).fill(0);
          this.cdRef.detectChanges();
        })
      )
      .subscribe();
  }

  @ViewChildren(CdkPortalOutlet) outlets!: QueryList<CdkPortalOutlet>;

  ngAfterViewInit() {
    this.outlets.changes.subscribe(() => {
      console.log('outlets length', this.outlets.length);
      const componentPortal = new ComponentPortal(QuoteScheduleComponent);
      this.outlets.forEach((outlet, idx) => {
        const componentRef = outlet.attachComponentPortal(componentPortal);

        const [id, entry] = this.portalService.getFirstUnattachedEntry();

        this.portalService.updateEntry(id, {
          portal: componentPortal,
          componentRef,
          data: entry.data
        });

        outlet.attached.subscribe(() => {
          console.log(`Outlet ${idx} attached manually`);
        });
      });
    });
  }

  private handleConnectionStateChange() {
    const state = this.medusaConnectionState();

    this.medusaConnectionCountdown = null;
    clearTimeout(this.medusaConnectionCountdownTimer);

    const countdown = (i: number) => {
      this.medusaConnectionCountdown = `${i}s`;
      if (i > 3) {
        this.medusaConnectionStateIntensity = 1.0;
      }
      this.medusaConnectionCountdownTimer = setTimeout(() => {
        countdown(i + 1);
      }, 1000);
    };

    switch (state) {
      case 'DISCONNECTED':
        this.medusaConnectionStateIntensity = 1.0;
        break;
      case 'CONNECTING':
        this.medusaConnectionStateIntensity = 0.5;
        countdown(0);
        break;
      default:
        this.medusaConnectionStateIntensity = 0.0;
        break;
    }
  }

  private pageVisibilityListener() {
    document.addEventListener('visibilitychange', () => {
      const isLoggedIn = !!this.medusaAccount.entity;
      console.log(
        `visibiltyChange: ${document.visibilityState} [${isLoggedIn}]`
      );
      if (document.visibilityState === 'visible' && isLoggedIn) {
        this.throttledMedusaLogin(this.medusaAccount?.entity);
      }
    });
  }

  private activityListener() {
    const MINS = 1000 * 60;
    const HOURS = MINS * 60;
    const pollInterval = 2000;
    const maxInactiveTime = 4 * HOURS;
    const pendingInactiveTime = maxInactiveTime - 5 * MINS;
    let timeOfLastActivity = Date.now();
    this.subsink.sink = merge(
      fromEvent(window, 'mousemove'),
      fromEvent(document, 'visibilitychange').pipe(
        filter(() => document.visibilityState === 'visible')
      )
    )
      .pipe(throttleTime(1000))
      .subscribe(() => {
        this.activityState = 'ACTIVE';
        timeOfLastActivity = Date.now();
      });

    this.subsink.sink = interval(pollInterval).subscribe(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - timeOfLastActivity;
      if (timeSinceLastActivity > maxInactiveTime) {
        this.activityState = 'IDLE';
        console.info('Idle, signing out');
        this.signOut();
      } else if (timeSinceLastActivity > pendingInactiveTime) {
        this.activityState = 'PENDING';
      } else {
        this.activityState = 'ACTIVE';
      }
    });
  }

  public async setupRaygun() {
    if (!useRaygun()) {
      return;
    }
    const cognito = await this.authService.currentSession();
    const token = cognito.getIdToken().getJwtToken();
    const meta = JSON.parse(window.atob(token.split('.')[1]));
    rg4js('setUser', {
      identifier: meta.email,
      isAnonymous: false
    });
  }

  public toggleSounds() {
    this.playSounds = !this.playSounds;
    const currentPrefs = this.userStorageService.getItem<UserPreferencesState>(
      UserPreferencesStateKey
    );
    currentPrefs.OtherSettings.sounds = this.playSounds;
    this.userStorageService.setItem(UserPreferencesStateKey, currentPrefs);
  }

  public get showStructuringPanel() {
    return this.componentVisibilityService.showStructuringPanel;
  }

  public set showStructuringPanel(state: boolean) {
    this.componentVisibilityService.showStructuringPanel = state;
  }

  /**
   * In local dev mode, load some strategies to speed up development
   */
  private async loadTestStrategy() {
    console.log('Loading default strategy and user');
    try {
      let data: any = (await import('./default-strategy')).default;
      if (!data) {
        return;
      }
      await sleep(2000);

      if (data.strategy) {
        this.strategiesService
          .getStrategies({
            task: 'getStrategy',
            table: 'strategies-dev',
            ...data.strategy
          })
          .subscribe({
            next: strategy => this.onLoadStrategies(strategy)
          });
      }
      if (data.user) {
        await sleep(2000);
        try {
          this.medusaLogin(data.user, true);
        } catch (e) {
          // This causes lots of fake errors in Raygun
          console.log('Ignoring login error', e?.message);
        }
      }
    } catch (err) {
      console.error('loadTestStrategy', err);
    }
  }

  private doSwUpdate() {
    this.subsink.sink = this.swUpdate
      .start()
      .pipe(
        tap(() => {
          console.log('Starting Service Worker Update service');
        })
      )
      .subscribe((evt: any) => {
        const event = evt as VersionReadyEvent;
        console.log(event.type);
        console.log('current version is', event.currentVersion);
        console.log('available version is', event.latestVersion);
        this.alertsService.addAlert({
          message:
            'New version available - please close and re-open browser to update',
          skipLog: true,
          type: 'info'
        });
      });
  }

  get userInfo() {
    return this.adminService.userInfo;
  }

  get warningScreenData() {
    return this.rfqService.warningScreenData;
  }

  warningScreenResponse() {
    this.rfqService.warningScreenData.warningScreenOpen = false;
  }

  warningOpenChanged(val: boolean) {
    this.modalZIndex = this.winService.getMaxZIndex();
    this.warningScreenData.warningScreenOpen = val;
  }

  async signOut() {
    this.authService.logout();
  }

  onOpenUserPrefsChanged(event) {
    this.modalZIndex = this.winService.getMaxZIndex();
    this.userPrefOpen = event;
  }

  get showFixings() {
    return this.marketsService.showFixings;
  }

  public get showIRFixings(): boolean {
    return this.marketsService.showIRFixings;
  }

  get showCreditLines() {
    return this.marketsService.showCreditLines;
  }

  get showEntities() {
    return this.tradesCaptureService.showEntities;
  }

  get showCreditCurves() {
    return this.tradesCaptureService.showCreditCurves;
  }

  get showCsa() {
    return this.tradesCaptureService.showCsa;
  }

  get showTreasuryCurves() {
    return this.tradesCaptureService.showTreasuryCurves;
  }

  get showScheduleReports() {
    return this.tradesCaptureService.showScheduleReports;
  }

  get showAlerts() {
    return this.tradesCaptureService.showAlerts;
  }

  get rfqTabs() {
    return this.rfqService.rfqTabs;
  }

  get directRfqs() {
    return this.rfqService.directRfqs;
  }

  get providerQuoteTabs() {
    return this.providerQuoteService.tabs;
  }

  get showDciGrid() {
    return this.marketsService.showDci;
  }

  signOutOfTradingAccount() {
    if (this.medusaAccount.entity) {
      this.rfqService.logoutAndClearRfq();
    }
  }

  public medusaLogin(entity: string, logUserIn = true) {
    // called when medusa is to be logged in
    this.canLogin = false;
    setTimeout(() => (this.canLogin = true), 2000);
    if (logUserIn) {
      this.medusaAccount.entity = entity;
      this.rfqService.logout();
      if (entity) {
        this.rfqService.loginWithEntity(entity);
      }
    }
  }

  public maybeLogUserIn() {
    const login = () => {
      if (this.adminService.medusaCredentials.length === 1) {
        this.medusaLogin(this.adminService.medusaCredentials[0].entity);
      }
    };

    // Maybe already loaded
    if (this.adminService.medusaCredentials) {
      login();
      return;
    }

    this.configService.getMedusaCredentials().subscribe(medusaCredentials => {
      this.adminService.medusaCredentials = medusaCredentials;
      login();
    });
  }

  @Throttle(2000)
  public throttledMedusaLogin(entity: string, logUserIn = true) {
    this.rfqService.reLoginRequired = false;
    if (!this.canLogin) {
      return;
    }
    this.rfqService.userRole('prelogin').subscribe({
      next: r => {
        if (!r.result) {
          console.log('Re-logging in to Medusa [1]');
          this.medusaLogin(entity, logUserIn);
        } else {
          console.log('Not re-logging in to Medusa - already logged in');
        }
      },
      error: () => {
        console.log('Re-logging in to Medusa [2]');
        this.medusaLogin(entity, logUserIn);
      }
    });
  }

  get medusaCredentials() {
    return this.adminService.medusaCredentials;
  }

  get showMedusaLoginErrorModal() {
    return this.adminService.showMedusaLoginErrorModal;
  }

  get medusaAccount() {
    return this.adminService.medusaAccount;
  }

  positionsDataActive(): boolean {
    return this.componentVisibilityService.showPositionGrid;
  }

  marketsDataActive(): boolean {
    return (
      this.componentVisibilityService.volatilitySurfaceArray.length > 0 ||
      this.componentVisibilityService.historicalGraphArray.length > 0 ||
      this.componentVisibilityService.forwardCurveArray.length > 0
    );
  }

  onLoadStrategies(strategy: Strategy, newProduct = false) {
    strategy.products.forEach((product: any) => {
      this.strategyDetailService.addLegToStructuringScreen(
        product.shortName || product.productType,
        newProduct,
        product,
        this.strategyDetailService.tradingMode
      );
    });
  }

  // strategies are radio button (single select)
  onDeleteStrategy(strategy: Strategy) {
    // pass in the dynamodb partition key (userId) and sort key (timestamp)
    this.strategiesService.deleteStrategy(strategy.timestamp);
  }

  public dragStart(): void {
    this.isDragging = true;
    const draggableElement = document.querySelector(
      '#positions-split-area .as-split-gutter'
    ) as HTMLElement;
    if (draggableElement) {
      draggableElement.style.zIndex = '9999';
    } else {
      console.error('Could not increase z-index');
    }
  }

  public dragEnd(): void {
    this.isDragging = false;
    const draggableElement = document.querySelector(
      '#positions-split-area .as-split-gutter'
    ) as HTMLElement;
    if (draggableElement) {
      draggableElement.style.zIndex = '0';
    } else {
      console.error('Could not decrease z-index');
    }
  }

  get showStrategiesGrid() {
    return this.componentVisibilityService.showStrategiesGrid;
  }

  public get showFixingSources(): boolean {
    return this.displayDefinitionsService.showFixingSources;
  }

  public get showCurrencyPairs(): boolean {
    return this.displayDefinitionsService.showCurrencyPairs;
  }

  public get showCutoffs(): boolean {
    return this.displayDefinitionsService.showCutoffs;
  }

  public get showTradeStatus(): boolean {
    return this.displayDefinitionsService.showTradeStatus;
  }

  public createRfq(): void {
    this.rfqService.addNewTab(null, null, null, false);
  }

  @Throttle(1000)
  get showRfqButton(): boolean {
    return (
      this.rfqService.showRfq &&
      this.rfqService.executionContext?.executionTypes?.[0]?.shortName !==
        'genericMultiHedge'
    );
  }

  @Throttle(1000)
  get isDirectRfqDisabled(): boolean {
    return this.rfqService.isRfqDisabled;
  }

  private handleStrategiesListener(): void {
    const sub = this.globalSocket
      .getListener<Strategy>({ action: 'strategies' })
      .subscribe({
        next: message => {
          this.strategiesService
            .newStrategyReceived(message.payload)
            .subscribe({
              next: () => this.handleNewStrategy(message.payload)
            });
        },
        error: async () => {
          sub.unsubscribe();
          await sleep(1000);
          this.notificationListener();
        },
        complete: async () => {
          await sleep(1000);
          this.handleStrategiesListener();
        }
      });
    this.subsink.sink = sub;
  }

  private notificationListener(): void {
    const sub = this.globalSocket
      .getListener<NotificationPayload>({ action: 'notification' })
      .subscribe({
        next: message => {
          this.alertsService.addAlert({
            message: message.payload.messageContent,
            type: message.payload.messageType,
            config: {
              disableTimeOut: true,
              closeButton: true,
              tapToDismiss: false
            }
          });
          this.userStorageService.ping();
        },
        error: async () => {
          sub.unsubscribe();
          await sleep(1000);
          this.notificationListener();
        },
        complete: async () => {
          await sleep(1000);
          this.notificationListener();
        }
      });
    this.subsink.sink = sub;
  }

  private handleTradeBookedListener(): void {
    const sub = this.globalSocket
      .getListener<TradeBookedNotificationPayload>({
        action: 'tradeNotification'
      })
      .subscribe({
        next: message => {
          const msg =
            message.payload.legs
              ?.map(m => m.tradeId || 'Unknown tradeId')
              .join(', ') || '';
          this.alertsService
            .addAlert({
              // Messages need to be unique, so we use the tradeIds
              message: `Trade booked [${msg}]. Click to load.`,
              type: 'success',
              config: { disableTimeOut: true, closeButton: true }
            })
            .onTap.subscribe(() => {
              const tradeIds = (
                message.payload.legs?.map(m => m.tradeId) || []
              ).filter(Boolean);
              if (tradeIds.length === 0) {
                return;
              }
              this.strategyDetailService.setTabTradingMode(true);
              this.tradesCaptureService
                .getTradesByTradeIds(tradeIds)
                .subscribe(trades => {
                  trades.forEach((trade: Trade) => {
                    this.strategyDetailService.addLegToStructuringScreen(
                      trade.productType,
                      false,
                      trade,
                      true
                    );
                  });
                });
            });
        },
        error: async () => {
          sub.unsubscribe();
          await sleep(1000);
          this.notificationListener();
        },
        complete: async () => {
          await sleep(1000);
          this.handleTradeBookedListener();
        }
      });
    this.subsink.sink = sub;
  }

  private handleNewStrategy(strategy: Strategy): void {
    this.componentVisibilityService.showStrategiesGrid = true;
    setTimeout(() => {
      this.strategiesViewerComponent.newStrategyReceived(strategy);
    }, 0);
  }

  private async isApiWorking() {
    try {
      await firstValueFrom(this.configService.getMedusaCredentials());
      console.log('Medusa credentials OK');
    } catch (e) {
      console.error('Medusa credentials failed', e);
    }
  }
}
